import type {
	FilterRuleModel,
	HTTPValidationError,
	OptimizationActionResponseModel,
} from '@/shared/generated'
import type { FieldOption, UpdateProductsByFieldsSchema } from '../model/types'

import { useCallback, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { RiAlertFill, RiInformationFill } from '@remixicon/react'
import { AxiosError } from 'axios'
import { fromPairs, map, prop } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'
import {
	FINAL_FIELDS,
	useBulkFinalFieldsMutation,
} from '@/entities/optimization'

import { ValidationErrorsList } from '@/shared/components/common/ValidationErrorsList'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import { Operators } from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemMedia,
} from '@/shared/components/ui/item'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { HiddenOnContext } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import * as Field from '@/shared/lib/fields/model'

import { updateProductsByFieldsSchema } from '../model/contract'
import { OptimizationActionsResponseList } from './OptimizationActionsResponseList'
import { FieldsList } from './update-by-fields/FieldsList'
import { LoadingContent } from './update-by-fields/LoadingContent'
import { PendingContent } from './update-by-fields/PendingContent'
import { Prediction } from './update-by-fields/Prediction'
import { SkusTextarea } from './update-by-fields/SkusTextarea'

type ProductsBulkModalProps = {
	filters: FilterRuleModel[]
	isOpen: boolean
	close(): void
}

export function AssortmentUpdateModal({
	filters,
	isOpen,
	close,
}: ProductsBulkModalProps) {
	const { t } = useTranslation()

	const fieldsQuery = useColumnsSchemaQuery(HiddenOnContext.Products)
	const updateFinalFieldsMutation = useBulkFinalFieldsMutation()
	const fields = useMemo(
		(): FieldOption[] =>
			fieldsQuery.data
				?.filter(prop('params.editable'))
				.filter((schema) => FINAL_FIELDS.includes(schema.id))
				.map((schema): FieldOption => ({
					value: schema.id,
					label: t(schema.name, {
						ns: 'fields',
						defaultValue: schema.name,
					}),
					type: schema.value_type,
					field_type: schema.field_type!,
					schema,
				})) || [],
		[fieldsQuery.data, t]
	)
	const fieldsMap = useMemo(
		() => fromPairs(map((field) => [field.value, field], fields || [])),
		[fields]
	)
	const getField = useCallback((value: string) => fieldsMap[value], [fieldsMap])

	const byFilters = filters?.length > 0
	const methods = useForm({
		defaultValues: { fields: [], skus: [] },
		resolver: zodResolver(updateProductsByFieldsSchema),
	})
	const errorsList = updateFinalFieldsMutation.error?.response?.data.detail
	const isLoading = fieldsQuery.isLoading
	const isError = updateFinalFieldsMutation.isError
	const isPending = updateFinalFieldsMutation.isPending
	const isSuccess = updateFinalFieldsMutation.isSuccess

	const onSubmit = useCallback(
		async (data: UpdateProductsByFieldsSchema) => {
			ampli.tableManualUpdate({
				'Amount of fields for updating': data.fields.length,
			})

			const values = data.fields
				.map(({ value, inputValue }) => {
					const def = getField(value)
					const field = Field.bindModel(
						{
							id: def.value,
							// TODO: align with the field type
							type: def.field_type as any,
							name: def.label,
							property: null,
						},
						{} as any
					)

					return [value, field.coerceValue(inputValue)]
				})
				.reduce(
					(acc, [name, value]) => {
						acc[name] = value

						return acc
					},
					{} as Record<string, any>
				)
			if (!byFilters) {
				await updateFinalFieldsMutation.mutateAsync([
					{
						filters: [
							...filters,
							data.skus?.length
								? { name: 'sku', operation: Operators.IN, value: data.skus }
								: undefined,
						].filter(Boolean) as FilterRuleModel[],
						final_fields: values,
					},
				])
			} else {
				await updateFinalFieldsMutation.mutateAsync([
					{ filters, final_fields: values },
				])
			}
		},
		[filters, updateFinalFieldsMutation, getField, byFilters]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				className={
					isLoading || isPending
						? 'max-w-sm'
						: byFilters
							? 'max-w-3xl'
							: 'max-w-5xl'
				}
			>
				<FormProvider
					methods={methods}
					onSubmit={methods.handleSubmit(onSubmit)}
					className='flex flex-col gap-4'
				>
					<ErrorBoundary>
						{isLoading ? (
							<LoadingContent />
						) : isPending ? (
							<PendingContent />
						) : isSuccess ? (
							<ImportPartiallySuccessful
								data={updateFinalFieldsMutation.data}
							/>
						) : isError ? (
							<ImportError error={updateFinalFieldsMutation.error} />
						) : (
							<div className='flex gap-4'>
								{!byFilters && <SkusTextarea />}
								<div className='flex w-full flex-col gap-4'>
									<ModalHeader>
										<ModalTitle>
											{`${t('bulk_modal_bulk_update')} `}
											<Prediction filters={filters} />
										</ModalTitle>
										<ModalDescription>
											{t('bulk_modal_bulk_update_description')}
										</ModalDescription>
									</ModalHeader>
									<ModalBody className='max-h-[90vh] min-h-75'>
										<FieldsList
											fields={fields || []}
											getField={getField}
											getFieldOptions={() => []}
										/>
										{isError &&
											(errorsList?.length ? (
												<>
													<Alert variant='destructive' className='mt-4'>
														<AlertTriangleIcon />
														<AlertDescription>
															{t('common.error.request_failed_description', {
																defaultValue:
																	'The request failed. Please try again.',
															})}
														</AlertDescription>
													</Alert>
													<div className='mt-2 mb-auto flex w-full flex-col gap-2'>
														<ValidationErrorsList errors={errorsList} />
													</div>
												</>
											) : (
												<Alert variant='destructive' className='mt-5'>
													<AlertTriangleIcon />
													<AlertDescription>
														{t('error.unrecoverable.title')}
													</AlertDescription>
												</Alert>
											))}
									</ModalBody>
									<ModalFooter className='justify-between'>
										<ModalClose asChild>
											<Button data-testid='products-bulk-update-modal-cancel-button'>
												{t('general_cancel')}
											</Button>
										</ModalClose>
										<Button
											variant='primary'
											onClick={methods.handleSubmit(onSubmit)}
											data-testid='products-bulk-update-modal-apply-button'
										>
											{t('general_apply')}
										</Button>
									</ModalFooter>
								</div>
							</div>
						)}
					</ErrorBoundary>
				</FormProvider>
			</ModalContent>
		</Modal>
	)
}

const ImportPartiallySuccessful = ({
	data,
}: {
	data: OptimizationActionResponseModel[]
}) => {
	const { t } = useTranslation()

	const isFullySuccessful = data.every((item) => item.success)

	return (
		<>
			<ModalHeader>
				<ModalTitle>
					{t('update_products_modal.results.title', {
						defaultValue: 'Bulk update results',
					})}
				</ModalTitle>
				<ModalDescription>
					{t('update_products_modal.results.description', {
						defaultValue:
							'The following optimizations were affected by the update.',
					})}
				</ModalDescription>
			</ModalHeader>
			{!data?.length && (
				<Item size='xs' variant='muted'>
					<ItemMedia variant='icon'>
						<RiAlertFill className='text-yellow-500' />
					</ItemMedia>
					<ItemContent>
						<ItemDescription>
							{t('import.no_changes_applied', {
								defaultValue: 'No changes were applied to any optimizations.',
							})}
						</ItemDescription>
					</ItemContent>
				</Item>
			)}
			<div className='mb-auto h-72 w-full scroll-fade overflow-y-auto'>
				<OptimizationActionsResponseList data={data} />
			</div>
			{isFullySuccessful && (
				<Item size='xs' variant='muted' className='bg-green-500/10'>
					<ItemMedia variant='icon'>
						<RiInformationFill className='text-green-500' />
					</ItemMedia>
					<ItemContent>
						<ItemDescription>
							{t('import.fully_success.description', {
								defaultValue:
									'Your changes will be applied in the background. I could take up to 1 minute to complete.',
							})}
						</ItemDescription>
					</ItemContent>
				</Item>
			)}
			{!isFullySuccessful && (
				<Item size='xs' variant='muted' className='bg-yellow-500/10'>
					<ItemMedia variant='icon'>
						<RiAlertFill className='text-yellow-500' />
					</ItemMedia>
					<ItemContent>
						<ItemDescription>
							{t('import.partially_success.description', {
								defaultValue:
									"Some optimizations were not updated successfully. Bulk update is only applicable to OGs in 'Finished' status. Please check the logs for more details.",
							})}
						</ItemDescription>
					</ItemContent>
				</Item>
			)}
			<ModalFooter className='justify-between'>
				<ModalClose asChild>
					<Button
						variant='primary'
						data-testid='products-bulk-update-modal-cancel-button'
					>
						{t('general_done', {
							defaultValue: 'Done',
						})}
					</Button>
				</ModalClose>
			</ModalFooter>
		</>
	)
}
const ImportError = ({
	error = null,
}: {
	error: AxiosError<HTTPValidationError> | null
}) => {
	const { t } = useTranslation()

	if (error instanceof AxiosError) {
		if (error.response?.status === 422) {
			const errors = error?.response?.data?.detail || []

			return (
				<>
					<ModalTitle>
						{t('update_products_modal.error.title', {
							defaultValue: 'Bulk update error',
						})}
					</ModalTitle>
					<div className='mb-auto flex w-full flex-col gap-2'>
						<ValidationErrorsList errors={errors} />
					</div>
					<Item size='xs' variant='muted'>
						<ItemMedia variant='icon'>
							<RiAlertFill className='text-yellow-500' />
						</ItemMedia>
						<ItemContent>
							<ItemDescription>
								{t('import.validation_errors.description', {
									defaultValue:
										'Some validation errors occurred. Please check the logs for more details.',
								})}
							</ItemDescription>
						</ItemContent>
					</Item>
				</>
			)
		}
	}

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon' className='bg-destructive/20'>
					<AlertTriangleIcon className='text-destructive' />
				</EmptyMedia>
				<EmptyTitle>{t('og_bulk_import_error')}</EmptyTitle>
				<EmptyDescription>{t('og_bulk_import_canceled')}</EmptyDescription>
			</EmptyHeader>
			<EmptyContent>
				<div className='prose prose-sm grid w-full rounded-md bg-muted/30 p-2 text-left text-xs prose-neutral dark:prose-invert'>
					<h5>{t('general_possible_reasons')}:</h5>
					<div
						dangerouslySetInnerHTML={{
							__html: t('og_import_error_hint_html'),
						}}
					/>
				</div>
			</EmptyContent>
		</Empty>
	)
}
